import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import {
  useIntegriteJournal,
  useJournalAudit,
  type EntreeAuditDto,
} from '@/shared/api/hooksAdmin';
import { abrege, horodatage } from '@/shared/lib/utils';
import { Bouton } from '@/shared/ui/Bouton';
import { Saisie } from '@/shared/ui/Champ';
import { Carte, EnTeteSection, Pastille, Table, type ColonneTable } from '@/shared/ui/Table';

/**
 * Journal d'audit d'administration.
 *
 * LE MIROIR DE SECURITE DE TOUT LE RESTE (§4). Strictement en lecture : il n'existe aucune
 * route d'ecriture ni de suppression sur ce journal cote API, et donc rien a construire ici
 * pour en modifier une ligne. Le journal ne se remplit que par effet de bord des actes.
 *
 * <para>
 * LA VERIFICATION D'INTEGRITE EST EN TETE DE PAGE, avant les entrees elles-memes. Un
 * journal qu'on ne peut pas verifier n'est pas une preuve, c'est une liste : la chaine
 * d'empreintes est ce qui fait la difference, et son etat doit se lire d'un coup d'oeil.
 * </para>
 */
export default function JournalAuditPage() {
  const [entrepriseId, setEntrepriseId] = useState('');
  const [action, setAction] = useState('');
  const [donneesPersonnellesSeulement, setDonneesPersonnelles] = useState(false);

  const { data, isPending, isError, error, refetch } = useJournalAudit({
    entrepriseId: entrepriseId.trim() || undefined,
    action: action.trim() || undefined,
    donneesPersonnellesSeulement,
  });

  const integrite = useIntegriteJournal();
  const intacte = integrite.data?.chaineIntacte ?? true;

  const colonnes: readonly ColonneTable<EntreeAuditDto>[] = [
    {
      cle: 'rang',
      entete: '#',
      numerique: true,
      rendu: (ligne) => <span className="text-texte-faible">{ligne.numeroOrdre}</span>,
    },
    {
      cle: 'horodatage',
      entete: 'Horodatage',
      rendu: (ligne) => <span className="text-texte-doux">{horodatage(ligne.horodatage)}</span>,
    },
    {
      cle: 'administrateur',
      entete: 'Administrateur',
      rendu: (ligne) => <span className="text-texte">{ligne.administrateurEmail}</span>,
    },
    {
      cle: 'action',
      entete: 'Acte',
      rendu: (ligne) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <code className="text-texte">{ligne.action}</code>
          {ligne.donneesPersonnellesConsultees ? (
            <Pastille ton="sensible">Donnees personnelles</Pastille>
          ) : null}
        </span>
      ),
    },
    {
      cle: 'cible',
      entete: 'Cible',
      secondaire: true,
      rendu: (ligne) => (
        <span className="text-texte-doux">
          {ligne.typeCible}
          {ligne.cibleId ? ` · ${abrege(ligne.cibleId)}` : ''}
        </span>
      ),
    },
    {
      cle: 'entreprise',
      entete: 'Entreprise',
      secondaire: true,
      rendu: (ligne) => (
        <span className="text-texte-doux">{abrege(ligne.entrepriseConcernee)}</span>
      ),
    },
    {
      cle: 'motif',
      entete: 'Motif',
      secondaire: true,
      rendu: (ligne) => (
        <span className="text-texte-doux" title={ligne.motif ?? undefined}>
          {ligne.motif ? `${ligne.motif.slice(0, 60)}${ligne.motif.length > 60 ? '…' : ''}` : '—'}
        </span>
      ),
    },
    {
      cle: 'ip',
      entete: 'Origine',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-faible">{ligne.adresseIp ?? '—'}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="Journal d’audit"
        description="Tous les actes d’administration, dans l’ordre, sans exception."
      />

      <Carte
        className={
          intacte
            ? 'flex items-start justify-between gap-3 border-succes/30 bg-succes-voile p-3'
            : 'flex items-start justify-between gap-3 border-danger/50 bg-danger-voile p-3'
        }
      >
        <div className="flex items-start gap-2">
          {intacte ? (
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-succes" aria-hidden />
          ) : (
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden />
          )}

          <div>
            <p className="font-medium text-texte">
              {integrite.isPending
                ? 'Verification…'
                : intacte
                  ? `Chaine verifiee — ${integrite.data?.nombreEntrees ?? 0} entrees`
                  : 'Chaine rompue'}
            </p>

            <p className="text-texte-doux">
              {intacte
                ? 'Chaque entree porte l’empreinte de la precedente, et aucune n’a bouge.'
                : (integrite.data?.explication ??
                  'Une entree a ete alteree ou supprimee directement en base.')}
              {!intacte && integrite.data?.premierRangInvalide
                ? ` Premier rang en cause : ${integrite.data.premierRangInvalide}.`
                : ''}
            </p>
          </div>
        </div>

        <Bouton variante="secondaire" taille="sm" onClick={() => void integrite.refetch()}>
          Reverifier
        </Bouton>
      </Carte>

      <div className="flex flex-wrap items-center gap-2">
        <Saisie
          value={entrepriseId}
          onChange={(evenement) => setEntrepriseId(evenement.target.value)}
          placeholder="Identifiant d’entreprise…"
          aria-label="Filtrer par entreprise"
          className="max-w-64"
        />

        <Saisie
          value={action}
          onChange={(evenement) => setAction(evenement.target.value)}
          placeholder="admin.entreprise.suspendue…"
          aria-label="Filtrer par acte"
          className="max-w-64"
        />

        {/*
          Le filtre qui compte : en cas de controle ou de doute, la question posee est
          « qui a regarde des donnees de clients, et pourquoi ». Il est donc premier de
          rang, pas noye dans un menu.
        */}
        <Bouton
          variante={donneesPersonnellesSeulement ? 'secondaire' : 'discret'}
          taille="sm"
          aria-pressed={donneesPersonnellesSeulement}
          onClick={() => setDonneesPersonnelles((actuel) => !actuel)}
        >
          <ShieldAlert />
          Donnees personnelles seulement
        </Bouton>
      </div>

      <Table
        colonnes={colonnes}
        lignes={data}
        cleLigne={(ligne) => ligne.entreeId ?? String(ligne.numeroOrdre)}
        chargement={isPending}
        erreur={isError ? error : undefined}
        onReessayer={() => void refetch()}
        messageVide="Aucun acte ne correspond a ces filtres."
      />

      <p className="text-texte-faible">
        Journal en ajout seul : aucune route de l’API ne permet de modifier ni de supprimer
        une entree, y compris pour un administrateur.
      </p>
    </div>
  );
}
